import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { makeStyles, tokens } from "@fluentui/react-components";
import i18n from "@shared/i18n";
import { useAuth } from "../../../provider/authProvider";
import {
  useTicketUpdateTranslate,
  useDeleteTicketUpdate,
  useRetryTicketUpdateEmail,
} from "../hooks/useTicketUpdateMutations";
import TicketNoteCard from "./TicketNoteCard";
import TicketMailCard from "./TicketMailCard";

const useStyles = makeStyles({
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    margin: 0,
    padding: 0,
    gap: 0,
    boxShadow: "none",
  },
  header: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    padding: "8px",
  },
  body: {
    padding: "8px",
  },
  badge: {
    padding: "0 6px",
  },
  badgeIncoming: {
    backgroundColor: "#1E88E5",
    color: "#FFFFFF",
    "@media (prefers-color-scheme: dark)": {
      backgroundColor: "#64B5F6",
      color: "#FFFFFF",
    },
    padding: "0 6px",
  },
  badgeOutgoing: {
    backgroundColor: "#43A047",
    color: "#FFFFFF",
    "@media (prefers-color-scheme: dark)": {
      backgroundColor: "#81C784",
      color: "#FFFFFF",
    },
    padding: "0 6px",
  },
  badgeNote: {
    backgroundColor: "#6D6D6D",
    color: "#FFFFFF",
    "@media (prefers-color-scheme: dark)": {
      backgroundColor: "#BDBDBD",
      color: "#1F1F1F",
    },
    padding: "0 6px",
  },
});

export const TicketUpdateCard = ({ ticketUpdate, onEmailReply }) => {
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const translateMutation = useTicketUpdateTranslate();
  //const deleteMutation = useDeleteTicketUpdate(["TicketUpdates", ticketUpdate.ticket_id]);
  const retryMutation = useRetryTicketUpdateEmail(["TicketUpdates", ticketUpdate.ticket_id]);
  const [translatedContent, setTranslatedContent] = React.useState(ticketUpdate.translatedContent || null);
  const [showTranslation, setShowTranslation] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = React.useState(ticketUpdate.language);
  const [showEmailDetails, setShowEmailDetails] = React.useState(false);
  const attachmentInputRef = React.useRef(null);
  const styles = useStyles();

  const currentUILang = i18n.language || "en";
  const showTranslateLink = ticketUpdate.language && ticketUpdate.language !== currentUILang;

  const handleTranslate = () => {
    if (translatedContent) {
      setShowTranslation(!showTranslation);
      return;
    }

    translateMutation.mutate(
      {
        content: ticketUpdate.content,
        sourceLanguage: ticketUpdate.language,
        targetLanguage: currentUILang,
      },
      {
        onSuccess: (data) => {
          const translated = data.translatedText || data.translated_text;
          setTranslatedContent(translated);
          setShowTranslation(true);
        },
      },
    );
  };

  const getSharedCardProps = () => ({
    ticketUpdate,
    t,
    styles,
    handleTranslate: showTranslateLink ? handleTranslate : null, // pass handleTranslate only if the link should be shown
    translateMutation,
    showTranslation,
    translatedContent,
    selectedLanguage,
    setSelectedLanguage,
    attachmentInputRef,
    useDeleteTicketUpdate,
  });

  switch (ticketUpdate.update_type) {
    case "note":
      return <TicketNoteCard {...getSharedCardProps()} currentUser={currentUser} />;

    case "outgoing_email":
    case "incoming_email":
      return (
        <TicketMailCard
          {...getSharedCardProps()}
          showEmailDetails={showEmailDetails}
          setShowEmailDetails={setShowEmailDetails}
          retryMutation={retryMutation}
          onEmailReply={onEmailReply}
        />
      );

    default:
      return (
        <div
          className={styles.card}
          style={{ display: "flex", flexDirection: "column", minHeight: "auto", overflow: "visible" }}
        >
          <span>{t("generic.notSupported", { defaultValue: "Not supported" })}</span>
        </div>
      );
  }
};

export default TicketUpdateCard;
